import { message } from "antd";
import { BHistoryList } from "components/base";
import { observer } from "mobx-react";
import { useCallback, useMemo, useState } from "react";
import { chatStore } from "store/chatStore";
import DeleteConversationModal from "./DeleteConversationModal";
import "./SidebarChatList.scss";

type SidebarChatListProps = {
  onNavigate?: () => void;
};

type PendingDelete = {
  id: number;
  name: string;
};

const SidebarChatList = observer(function SidebarChatList({
  onNavigate,
}: SidebarChatListProps) {
  const { filteredConversations, selectedConversationId } = chatStore;
  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const items = useMemo(
    () =>
      filteredConversations.map((c) => ({
        id: String(c.id),
        title: c.name || `Cuộc trò chuyện #${c.id}`,
      })),
    [filteredConversations],
  );

  const handleSelect = useCallback(
    (id: string) => {
      void chatStore.selectConversation(Number(id));
      onNavigate?.();
    },
    [onNavigate],
  );

  const handleRename = useCallback(async (id: string, name: string) => {
    try {
      await chatStore.renameConversation(Number(id), name);
    } catch {
      message.error("Không thể đổi tên cuộc trò chuyện");
    }
  }, []);

  const handleDeleteRequest = useCallback(
    (id: string) => {
      const conversation = filteredConversations.find((entry) => String(entry.id) === id);
      if (!conversation) {
        return;
      }
      setPendingDelete({
        id: conversation.id,
        name: conversation.name || `Cuộc trò chuyện #${conversation.id}`,
      });
    },
    [filteredConversations],
  );

  const handleCloseDeleteModal = useCallback(() => {
    if (isDeleting) {
      return;
    }
    setPendingDelete(null);
  }, [isDeleting]);

  const handleConfirmDelete = useCallback(async () => {
    if (!pendingDelete) {
      return;
    }
    setIsDeleting(true);
    try {
      await chatStore.deleteConversation(pendingDelete.id);
      setPendingDelete(null);
    } catch {
      message.error("Không thể xóa cuộc trò chuyện");
    } finally {
      setIsDeleting(false);
    }
  }, [pendingDelete]);

  return (
    <div className="sidebar-chat-list">
      <BHistoryList
        items={items}
        activeId={selectedConversationId != null ? String(selectedConversationId) : null}
        title="Cuộc trò chuyện"
        emptyDescription="Chưa có chat trong dự án này"
        onSelect={handleSelect}
        onRename={handleRename}
        onDeleteRequest={handleDeleteRequest}
      />

      <DeleteConversationModal
        open={pendingDelete != null}
        conversationName={pendingDelete?.name ?? ""}
        loading={isDeleting}
        onCancel={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
});

export default SidebarChatList;
